import React, { useRef } from 'react';

/**
 * 01 PICK — a photo, a clip, or nothing at all (handoff v3 §3). Three filled 3:4 tiles on a
 * black panel: + PHOTO OR VIDEO (the native picker), what they already chose, Aa TEXT ONLY.
 *
 * The file is read locally (object URL) and stays on the device. Nothing reaches the server at
 * this step or any later one; the upload starts when they press SEND.
 */
function Tile({ selected, onClick, children, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={selected}
      className="relative flex flex-col items-center justify-center overflow-hidden text-white"
      style={{ aspectRatio: '3 / 4', gap: 10, padding: 0, background: '#1A1A1A', border: 0, outline: selected ? '2px solid #00FFFF' : 'none', outlineOffset: -2, fontFamily: 'inherit', cursor: 'pointer' }}
    >
      {children}
      {selected && <span aria-hidden data-testid="pick-dot" style={{ position: 'absolute', right: 8, top: 8, width: 16, height: 16, borderRadius: '50%', background: '#00FFFF' }} />}
    </button>
  );
}

export default function StepPick({ media, onPickFile, onPickText, desktop = false }) {
  const fileRef = useRef(null);
  const openPicker = () => fileRef.current?.click();
  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) onPickFile(file);
  };

  return (
    <div className="font-mono" onDragOver={desktop ? (e) => e.preventDefault() : undefined} onDrop={desktop ? onDrop : undefined} data-testid="pick-panel">
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        data-testid="pick-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickFile(f); e.target.value = ''; }}
      />
      <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: 'rgba(255,255,255,.7)' }}>RECENTS</span>
      <div className="grid grid-cols-3" style={{ marginTop: 14, gap: 8 }}>
        <Tile selected={false} onClick={openPicker} ariaLabel="Photo or video">
          <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, lineHeight: 1 }}>+</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textAlign: 'center' }}>PHOTO OR<br />VIDEO</span>
        </Tile>

        {media?.localUrl && (
          <Tile selected={media.kind !== 'text'} onClick={() => {}} ariaLabel={media.name || 'Selected media'}>
            {media.kind === 'photo'
              ? <img src={media.localUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
              : <video src={media.localUrl} muted playsInline className="absolute inset-0 h-full w-full object-cover" />}
            {media.badge && (
              <span style={{ position: 'absolute', left: 8, bottom: 8, background: 'rgba(0,0,0,.72)', padding: '3px 8px', borderRadius: 999, fontSize: 9, fontWeight: 700, letterSpacing: '0.1em' }}>{media.badge}</span>
            )}
          </Tile>
        )}

        <Tile selected={media?.kind === 'text'} onClick={onPickText} ariaLabel="Text only">
          <span style={{ fontFamily: "'Times New Roman', Times, serif", fontStyle: 'italic', fontSize: 32, lineHeight: 1 }}>Aa</span>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em' }}>TEXT ONLY</span>
        </Tile>
      </div>
    </div>
  );
}
